import axios from "axios"
import { useStoreActions, useStoreState } from "easy-peasy"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { beep } from "./beep.js"
import ErrorPopup from "./ErrorPopup.js"
import StatusPopup from "./StatusPopup.js"
import {
	CLIENT_USERS_URL,
	DOG_AGENT_ADD_URL,
	DOG_AGENT_DEL_URL,
	DOG_AGENT_LIST_URL,
	DOG_DEPUTY_ASSIGNMENTS_URL,
	DOG_TAGS_ASSIGN_URL,
	DOG_TAGS_AUTO_ASSIGN_URL,
	DOG_TAGS_GENERATE_URL,
	DOG_TAGS_INVENTORY_SUMMARY_URL,
	DOG_TAGS_UNASSIGN_URL,
} from "./Constants.js"

const DogTags = () => {
	const d = new Date()
	const navigate = useNavigate()
	// Tag Setup is locked to the current fiscal year (Jul 1 – Jun 30, labeled by its
	// start year) so tags can never be generated/assigned for the wrong year.
	const fiscalYear = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1

	// easy-peasy
	const Token = useStoreState((state) => state.Token)
	const Theme = useStoreState((state) => state.Theme)
	const textColor = Theme === 4 ? "#333333" : "white"
	const setLoggedIn = useStoreActions((actions) => actions.setLoggedIn)
	const setHeaderCity = useStoreActions((actions) => actions.setHeaderCity)
	const setHeaderUser = useStoreActions((actions) => actions.setHeaderUser)
	const setIsDeputy = useStoreActions((actions) => actions.setIsDeputy)
	const Lastname = useStoreState((state) => state.Lastname)
	const Firstname = useStoreState((state) => state.Firstname)

	// generate
	const [tagYear] = useState(fiscalYear)
	const [packetSize, setPacketSize] = useState(50)
	const [quantity, setQuantity] = useState(1500)
	const [startingTag, setStartingTag] = useState(1)
	const [batchExists, setBatchExists] = useState(false)
	const [batchWarning, setBatchWarning] = useState("")

	// auto-assign
	const [autoYear] = useState(fiscalYear)
	const [autoStatus, setAutoStatus] = useState("")
	const [autoError, setAutoError] = useState("")

	// assign staff
	const [clientUsers, setClientUsers] = useState([])
	const [assignStaffError, setAssignStaffError] = useState("")

	// assign
	const [agentList, setAgentList] = useState([])
	const [selectedDeputy, setselectedDeputy] = useState("")
	const [staffKey, setStaffKey] = useState(0)

	// deputy assignments
	const [deputyAssignments, setDeputyAssignments] = useState([])

	// feedback
	const [error, setError] = useState("")
	const [generateStatus, setGenerateStatus] = useState("")
	const [assignStatus, setAssignStatus] = useState("")
	const [assignError, setAssignError] = useState("")

	// unassign
	const [unassignDeputy, setUnassignDeputy] = useState("")
	const [unassignStatus, setUnassignStatus] = useState("")
	const [unassignError, setUnassignError] = useState("")

	// inventory summary
	const [unassignedPacketsPrior, setUnassignedPacketsPrior] = useState(0)
	const [unassignedTagsPrior, setUnassignedTagsPrior] = useState(0)
	const [unassignedPackets, setUnassignedPackets] = useState(0)
	const [unassignedTags, setUnassignedTags] = useState(0)
	const [unassignedPacketsNext, setUnassignedPacketsNext] = useState(0)
	const [unassignedTagsNext, setUnassignedTagsNext] = useState(0)
	// unused (unassigned/assigned) tags from before the current fiscal year — the
	// maintenance-due signal; appears at the Jul 1 rollover, clears once purged
	const [oldUnusedTags, setOldUnusedTags] = useState(0)

	// show the prior fiscal-year column only while it still holds unassigned inventory
	const showPriorInventory = unassignedPacketsPrior > 0 || unassignedTagsPrior > 0

	// Load staff list and agent list on mount and after each successful assign
	useEffect(() => {
		async function loadStaff() {
			try {
				const request = { login_id: Token.login_id, client_id: Token.client_id }
				const { data } = await axios.post(DOG_AGENT_LIST_URL, request)
				setAgentList(data)
				// Live-sync the deputy flag with the authoritative list so add/remove of
				// yourself here updates the License-page banner with no re-login.
				const last = (Lastname || "").toLowerCase()
				const first = (Firstname || "").toLowerCase()
				if (last && first) {
					setIsDeputy(data.some((a) => (a.lastname ?? a[2] ?? "").toLowerCase() === last && (a.firstname ?? a[3] ?? "").toLowerCase() === first))
				}
			} catch (err) {
				setError(err.message)
			}
		}
		async function loadClientUsers() {
			try {
				const request = { login_id: Token.login_id, client_id: Token.client_id }
				const { data } = await axios.post(CLIENT_USERS_URL, request)
				setClientUsers(data)
			} catch (err) {
				setAssignStaffError(err.message)
			}
		}
		loadStaff()
		loadClientUsers()
	}, [Token.login_id, Token.client_id, tagYear, staffKey, setIsDeputy, Lastname, Firstname])

	useEffect(() => {
		async function loadInventorySummary() {
			try {
				const [priorRes, curRes, nextRes] = await Promise.all([
					axios.post(DOG_TAGS_INVENTORY_SUMMARY_URL, {
						login_id: Token.login_id,
						client_id: Token.client_id,
						tag_year: d.getFullYear() - 1,
					}),
					axios.post(DOG_TAGS_INVENTORY_SUMMARY_URL, {
						login_id: Token.login_id,
						client_id: Token.client_id,
						tag_year: d.getFullYear(),
					}),
					axios.post(DOG_TAGS_INVENTORY_SUMMARY_URL, {
						login_id: Token.login_id,
						client_id: Token.client_id,
						tag_year: d.getFullYear() + 1,
					}),
				])
				setUnassignedPacketsPrior(priorRes.data.unassigned_packets)
				setUnassignedTagsPrior(priorRes.data.unassigned_tags)
				setUnassignedPackets(curRes.data.unassigned_packets)
				setUnassignedTags(curRes.data.unassigned_tags)
				setUnassignedPacketsNext(nextRes.data.unassigned_packets)
				setUnassignedTagsNext(nextRes.data.unassigned_tags)
				setOldUnusedTags(curRes.data.old_unused_tags)
			} catch (err) {
				setError(err.message)
			}
		}
		loadInventorySummary()
	}, [Token.login_id, Token.client_id, staffKey])

	useEffect(() => {
		async function loadDeputyAssignments() {
			try {
				const currentYear = new Date().getFullYear()
				const [curRes, nextRes] = await Promise.all([
					axios.post(DOG_DEPUTY_ASSIGNMENTS_URL, {
						login_id: Token.login_id,
						client_id: Token.client_id,
						tag_year: currentYear,
					}),
					axios.post(DOG_DEPUTY_ASSIGNMENTS_URL, {
						login_id: Token.login_id,
						client_id: Token.client_id,
						tag_year: currentYear + 1,
					}),
				])
				const curWithYear = curRes.data.map((dep) => ({
					...dep,
					year: currentYear,
				}))
				const nextWithYear = nextRes.data.map((dep) => ({
					...dep,
					year: currentYear + 1,
				}))
				setDeputyAssignments([...curWithYear, ...nextWithYear])
			} catch (err) {
				setError(err.message)
			}
		}
		loadDeputyAssignments()
	}, [Token.login_id, Token.client_id, staffKey])

	// Error timeout / session redirect
	useEffect(() => {
		if (!error) return
		beep()
		const timer = setTimeout(() => {
			if (error.includes("401")) {
				setHeaderCity("CR Cloud Technology")
				setHeaderUser("None")
				setLoggedIn(false)
				navigate("/login")
			}
		}, 2000)
		return () => clearTimeout(timer)
	}, [error, navigate, setHeaderCity, setHeaderUser, setLoggedIn])

	useEffect(() => { if (autoError) beep() }, [autoError])
	useEffect(() => { if (assignError) beep() }, [assignError])
	useEffect(() => { if (unassignError) beep() }, [unassignError])
	useEffect(() => { if (batchWarning) beep() }, [batchWarning])

	async function handleAgentAdd(userId) {
		if (!userId) return
		try {
			const user = clientUsers.find((u) => String(u.id ?? u[0]) === String(userId))
			const lastname = user.lastname ?? user[2]
			const firstname = user.firstname ?? user[3]
			if (agentList.some((a) => (a.lastname ?? a[2]).toLowerCase() === lastname.toLowerCase() && (a.firstname ?? a[3]).toLowerCase() === firstname.toLowerCase())) return
			await axios.post(DOG_AGENT_ADD_URL, { login_id: Token.login_id, client_id: Token.client_id, client_user_id: Number(userId), lastname, firstname })
			setStaffKey((k) => k + 1)
		} catch (err) { setAssignStaffError(err.message) }
	}

	async function handleAgentDelete(agentId) {
		try {
			await axios.post(DOG_AGENT_DEL_URL, { login_id: Token.login_id, client_id: Token.client_id, agent_id: agentId })
			setStaffKey((k) => k + 1)
		} catch (err) {
			setAssignStaffError(err.message === "Request failed with status code 500" ? "Request failed because packets have been assigned." : err.message)
		}
	}

	async function handleGenerate(e) {
		e.preventDefault()
		setBatchExists(false)
		setGenerateStatus("submitting")
		try {
			const request = {
				login_id: Token.login_id,
				client_id: Token.client_id,
				tag_year: tagYear,
				packet_size: parseInt(packetSize),
				quantity: parseInt(quantity),
				starting_tag: parseInt(startingTag),
			}
			const { data: genData } = await axios.post(DOG_TAGS_GENERATE_URL, request)
			if (genData.warning) {
				setBatchExists(true)
				setBatchWarning(genData.warning)
				setGenerateStatus("")
			} else {
				setGenerateStatus("success")
				setStaffKey((k) => k + 1)
				setTimeout(() => setGenerateStatus(""), 4000)
			}
		} catch (err) {
			setGenerateStatus("")
			if (err.message === "Request failed with status code 401") {
				setError("Timeout")
			} else if (err.message === "Request failed with status code 409") {
				setBatchExists(true)
			} else if (err.message === "Request failed with status code 422") {
				setError("422")
			} else if (err.message === "Request failed with status code 500") {
				setError("500")
			} else {
				setError(err.message)
			}
		}
	}

	async function handleAutoAssign(e) {
		e.preventDefault()
		setAutoStatus("submitting")
		try {
			const request = {
				login_id: Token.login_id,
				client_id: Token.client_id,
				tag_year: autoYear,
			}
			const { data: autoData } = await axios.post(
				DOG_TAGS_AUTO_ASSIGN_URL,
				request,
			)
			if (autoData.packets_assigned === 0) {
				setAutoStatus("")
				setAutoError("No unassigned packets available.")
				setTimeout(() => setAutoError(""), 5000)
			} else {
				setAutoStatus("success")
				setStaffKey((k) => k + 1)
				setTimeout(() => setAutoStatus(""), 3000)
			}
		} catch (err) {
			setAutoStatus("")
			if (err.message === "Request failed with status code 401") {
				setAutoError("Timeout")
			} else if (err.message === "Request failed with status code 422") {
				setAutoError("422")
			} else if (err.message === "Request failed with status code 500") {
				setAutoError("500")
			} else {
				setAutoError(err.message)
			}
		}
	}

	async function handleAssign(e) {
		e.preventDefault()
		setAssignStatus("submitting")
		try {
			const request = {
				login_id: Token.login_id,
				client_id: Token.client_id,
				tag_year: tagYear,
				assigned_to: Number(selectedDeputy),
			}
			await axios.post(DOG_TAGS_ASSIGN_URL, request)
			setAssignStatus("success")
			setStaffKey((k) => k + 1)
			setTimeout(() => setAssignStatus(""), 3000)
		} catch (err) {
			setAssignStatus("")
			if (err.message === "Request failed with status code 401") {
				setAssignError("Timeout")
			} else if (err.message === "Request failed with status code 404") {
				setAssignError("No unassigned packets available.")
			} else if (err.message === "Request failed with status code 422") {
				setAssignError("422")
			} else if (err.message === "Request failed with status code 500") {
				setAssignError("500")
			} else {
				setAssignError(err.message)
			}
		}
	}

	async function handleUnassign(e) {
		e.preventDefault()
		setUnassignStatus("submitting")
		try {
			const request = {
				login_id: Token.login_id,
				client_id: Token.client_id,
				tag_year: tagYear,
				agent_id: Number(unassignDeputy),
			}
			await axios.post(DOG_TAGS_UNASSIGN_URL, request)
			setUnassignStatus("success")
			setStaffKey((k) => k + 1)
			setTimeout(() => setUnassignStatus(""), 3000)
		} catch (err) {
			setUnassignStatus("")
			if (err.message === "Request failed with status code 401") {
				setUnassignError("Timeout")
			} else if (err.message === "Request failed with status code 404") {
				setUnassignError("No assigned packets found for this deputy.")
			} else if (err.message === "Request failed with status code 422") {
				setUnassignError("422")
			} else if (err.message === "Request failed with status code 500") {
				setUnassignError("500")
			} else {
				setUnassignError(err.message)
			}
		}
	}


	return (
		<main className="Main dogtags-scope">
			<div style={{ display: "flex", gap: "2rem", alignItems: "flex-start", justifyContent: "center", flexWrap: "wrap" }}>
			<fieldset className="tag_assignstaff">
				<legend>Assign Staff as Deputies to Sell Tags</legend>
				<div style={{ display: "flex", gap: "2rem", alignItems: "flex-start" }}>
					<div>
						<label style={{ display: "block", marginBottom: "0.5rem", textAlign: "center" }}>
							<span style={{ color: textColor }}>Available Staff</span><br />
							<span style={{ fontSize: "0.8rem" }}>Click row to add</span>
						</label>
						<table>
							<thead>
								<tr>
									<th style={{ backgroundColor: "var(--table-header-grey)" }}>Last Name</th>
									<th style={{ backgroundColor: "var(--table-header-grey)" }}>First Name</th>
								</tr>
							</thead>
							<tbody>
								{clientUsers.map((u) => (
									<tr key={u[0]} onClick={() => { setAssignStaffError(""); handleAgentAdd(u[0]) }} style={{ cursor: "pointer" }}>
										<td style={{ height: "28px" }}>{u[2]}</td>
										<td style={{ height: "28px" }}>{u[3]}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div>
						<label style={{ display: "block", marginBottom: "0.5rem", textAlign: "center" }}>
							<span style={{ color: textColor }}>Assigned Deputies</span><br />
							<span style={{ fontSize: "0.8rem" }}>Click row to remove</span>
						</label>
						{agentList.length > 0 ? (
							<table>
								<thead>
									<tr>
										<th style={{ backgroundColor: "var(--table-header-grey)" }}>Last Name</th>
										<th style={{ backgroundColor: "var(--table-header-grey)" }}>First Name</th>
									</tr>
								</thead>
								<tbody>
									{agentList.map((a) => (
										<tr key={a.id ?? a[0]} onClick={() => handleAgentDelete(a.id ?? a[0])} style={{ cursor: "pointer" }}>
											<td style={{ height: "28px" }}>{a.lastname ?? a[2]}</td>
											<td style={{ height: "28px" }}>{a.firstname ?? a[3]}</td>
										</tr>
									))}
								</tbody>
							</table>
						) : (
							<StatusPopup message="No agents assigned." />
						)}
					</div>
				</div>
				<ErrorPopup
					message={
						assignStaffError === "Timeout" ? "Your session has timed out. Please login again." :
						assignStaffError === "422" ? "Unable to use browser refresh button." :
						assignStaffError
					}
					onClose={() => setAssignStaffError("")}
				/>
			</fieldset>
				<fieldset className="tag_generate">
					<legend>Load New Tags</legend>
					<form onSubmit={handleGenerate}>
						<span
							style={{
								display: "block",
								marginTop: "0.5rem",
								textAlign: "center",
							}}
						>
							<label style={{ display: "block" }}>
								Fiscal Year:
							</label>
							<div style={{ marginTop: "0.25rem" }}>
								July 1, {fiscalYear} - June 30, {fiscalYear + 1}
							</div>
							<br />
						</span>
						<span
							style={{
								display: "block",
								marginTop: "0.5rem",
								textAlign: "center",
							}}
						>
							<label htmlFor="gen_starting_tag" style={{ display: "block" }}>
								Starting Tag #:
							</label>
							<input
								type="text"
								id="gen_starting_tag"
								name="gen_starting_tag"
								value={startingTag}
								onFocus={(e) => e.target.select()}
								onChange={(e) => {
									const val = e.target.value
									if (/^\d*$/.test(val)) setStartingTag(val)
								}}
								onBlur={(e) => {
									const n = parseInt(e.target.value)
									if (!n || n <= 0) setStartingTag("")
								}}
								style={{ width: "9ch", padding: "5px", textAlign: "center" }}
							/>
						</span>
						<span
							style={{
								display: "block",
								marginTop: "0.5rem",
								textAlign: "center",
							}}
						>
							<label htmlFor="gen_packet" style={{ display: "block" }}>
								Packet Size:
							</label>
							<select
								id="gen_packet"
								name="gen_packet"
								value={packetSize}
								onChange={(e) => {
									setPacketSize(e.target.value)
									setError("")
									setGenerateStatus("")
								}}
							>
								<option value={50}>50</option>
								<option value={100}>100</option>
							</select>
						</span>
						<span
							style={{
								display: "block",
								marginTop: "0.5rem",
								textAlign: "center",
							}}
						>
							<label htmlFor="gen_qty" style={{ display: "block" }}>
								Quantity:
							</label>
							<input
								type="text"
								id="gen_qty"
								name="gen_qty"
								value={quantity}
								onFocus={(e) => e.target.select()}
								onChange={(e) => {
									if (/^\d*$/.test(e.target.value)) {
										setQuantity(e.target.value)
										setError("")
										setGenerateStatus("")
									}
								}}
								style={{ width: "9ch", padding: "5px", textAlign: "center" }}
							/>
						</span>
						<div className="InputItem" style={{ textAlign: "center" }}>
							<button
								className="saveBtn"
								type="submit"
								disabled={
									generateStatus === "submitting" ||
									batchExists ||
									Token.client_id === 0 ||
									!startingTag ||
									parseInt(startingTag) <= 0
								}
							>
								Load Tags
							</button>
						</div>
						<ErrorPopup message={batchWarning} onClose={() => { setBatchExists(false); setBatchWarning("") }} />
						{generateStatus === "success" && <StatusPopup message={`Batch of ${quantity} tags generated for ${tagYear}.`} />}
						<ErrorPopup
							message={
								error === "Timeout" ? "Your session has timed out. Please login again." :
								error === "422" ? "Unable to use browser refresh button." :
								error === "500" ? "Unknown error — notify CR Cloud Technology." :
								error
							}
							onClose={() => setError("")}
						/>
					</form>
				</fieldset>

				<fieldset className="tag_assign">
					<legend>Packet Assignments</legend>
					<span
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "0.5rem",
							marginBottom: "2rem",
						}}
					>
						<label>Fiscal Year:</label>
						<span>July 1, {fiscalYear} - June 30, {fiscalYear + 1}</span>
					</span>
					<div
						style={{
							display: "flex",
							gap: "1rem",
							alignItems: "flex-start",
						}}
					>
						<fieldset>
							<legend>Auto-Assign All</legend>
							<form onSubmit={handleAutoAssign}>
								<div style={{ color: textColor }}>One Packet/Deputy</div>
								<div className="InputItem">
									<button
										className="saveBtn"
										type="submit"
										style={{ width: "130px", marginLeft: "0px" }}
										disabled={autoStatus === "submitting"}
									>
										Auto-Assign
									</button>
								</div>
								{autoStatus === "success" && (
									<StatusPopup message="Packets assigned to all deputies." />
								)}
								<ErrorPopup message={autoError} onClose={() => setAutoError("")} />
							</form>
						</fieldset>
						<fieldset>
							<legend>Individual Assign</legend>
							<form onSubmit={handleAssign}>
								<span
									style={{
										display: "block",
										textAlign: "center",
									}}
								>
									<label htmlFor="assign_deputy" style={{ display: "block" }}>
										Select Deputy:
									</label>
									<select
										id="assign_deputy"
										name="assign_deputy"
										value={selectedDeputy}
										onChange={(e) => {
											setselectedDeputy(e.target.value)
											setError("")
											setAssignStatus("")
										}}
									>
										<option value=""></option>
										{agentList.map((a) => (
											<option key={a.id ?? a[0]} value={a.id ?? a[0]}>
												{a.lastname ?? a[2]}, {a.firstname ?? a[3]}
											</option>
										))}
									</select>
								</span>
								<div className="InputItem">
									<button
										className="saveBtn"
										type="submit"
										disabled={
											assignStatus === "submitting" ||
											!selectedDeputy ||
											Token.client_id === 0
										}
									>
										Assign
									</button>
								</div>
								{assignStatus === "success" && (
									<StatusPopup message="Tags assigned successfully." />
								)}
								<ErrorPopup message={assignError} onClose={() => setAssignError("")} />
							</form>
						</fieldset>
						<fieldset style={{ width: "fit-content" }}>
							<legend>Unassign Highest Packet</legend>
							<form onSubmit={handleUnassign}>
								<span style={{ display: "block", textAlign: "center" }}>
									<label htmlFor="unassign_deputy" style={{ display: "block" }}>
										Select Deputy:
									</label>
									<select
										id="unassign_deputy"
										name="unassign_deputy"
										value={unassignDeputy}
										onChange={(e) => {
											setUnassignDeputy(e.target.value)
											setUnassignError("")
											setUnassignStatus("")
										}}
									>
										<option value=""></option>
										{agentList.map((a) => (
											<option key={a.id ?? a[0]} value={a.id ?? a[0]}>
												{a.lastname ?? a[2]}, {a.firstname ?? a[3]}
											</option>
										))}
									</select>
								</span>
								<div style={{ marginLeft: "18%" }} className="InputItem">
									<button
										className="saveBtn"
										type="submit"
										disabled={
											unassignStatus === "submitting" ||
											!unassignDeputy ||
											Token.client_id === 0
										}
									>
										Un-Assign
									</button>
								</div>
								{unassignStatus === "success" && (
									<StatusPopup message="Packet returned to inventory." />
								)}
								<ErrorPopup
									message={
										unassignError === "Timeout" ? "Your session has timed out. Please login again." :
										unassignError === "422" ? "Unable to use browser refresh button." :
										unassignError === "500" ? "Unknown error — notify CR Cloud Technology." :
										unassignError
									}
									onClose={() => setUnassignError("")}
								/>
							</form>
						</fieldset>
					</div>
				</fieldset>
			</div>
			<div
				style={{
					display: "flex",
					gap: "1rem",
					marginTop: "1rem",
					alignItems: "flex-start",
				}}
			>
				<fieldset style={{ flex: 1 }}>
					<legend>Deputy Packet Assignments</legend>
					{deputyAssignments.length > 0 ? (
						<div style={{ height: "283px", overflowY: "auto" }}>
							<table>
								<thead>
									<tr>
										<th
											style={{
												position: "sticky",
												top: 0,
												backgroundColor: "var(--table-header-grey)",
												zIndex: 1,
												width: "1%",
												whiteSpace: "nowrap",
												paddingRight: "40px",
											}}
										>
											Deputy
										</th>
										<th
											style={{
												position: "sticky",
												top: 0,
												backgroundColor: "var(--table-header-grey)",
												zIndex: 1,
												width: "1%",
												whiteSpace: "nowrap",
												paddingRight: "40px",
											}}
										>
											Fiscal Year
										</th>
										<th
											style={{
												position: "sticky",
												top: 0,
												backgroundColor: "var(--table-header-grey)",
												zIndex: 1,
												width: "1%",
												whiteSpace: "nowrap",
												paddingRight: "40px",
											}}
										>
											<span style={{ display: "block", textAlign: "center" }}>Available Tags</span>
										</th>
										<th
											style={{
												position: "sticky",
												top: 0,
												backgroundColor: "var(--table-header-grey)",
												zIndex: 1,
											}}
										>
											Packets Assigned
										</th>
									</tr>
								</thead>
								<tbody>
									{deputyAssignments.map((dep) => (
										<tr key={`${dep.agent_id}-${dep.year}`}>
											<td style={{ whiteSpace: "nowrap", paddingRight: "40px" }}>
												{dep.lastname}, {dep.firstname}
											</td>
											<td style={{ whiteSpace: "nowrap", paddingRight: "40px" }}>{dep.year} - {dep.year + 1}</td>
											<td style={{ whiteSpace: "nowrap", paddingRight: "40px", textAlign: "center" }}>{dep.available_tags}</td>
											<td>
												{dep.packets.map((p) => (
													<span
														key={p}
														style={{
															display: "inline-block",
															margin: "2px 3px",
															padding: "2px 8px",
															borderRadius: "12px",
															backgroundColor: "#4d5166",
															color: "#fff",
															fontSize: "0.8em",
															fontWeight: "600",
														}}
													>
														{p}
													</span>
												))}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<StatusPopup message={`No packets assigned for ${tagYear}.`} />
					)}
				</fieldset>
				<div
					style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}
				>
					<fieldset style={{ width: "fit-content", textAlign: "center" }}>
						<legend>Unassigned Packet Inventory</legend>
						<table
							style={{
								borderCollapse: "collapse",
								marginTop: "0.5rem",
								border: "none",
							}}
						>
							<thead>
								<tr style={{ background: "transparent" }}>
									<td style={{ padding: "4px 8px", border: "none" }}></td>
									{showPriorInventory && (
										<td
											style={{
												padding: "4px 8px",
												textAlign: "center",
												border: "none",
											}}
										>
											<strong style={{ color: textColor }}>
												{d.getFullYear() - 1} - {d.getFullYear()}
											</strong>
										</td>
									)}
									<td
										style={{
											padding: "4px 8px",
											textAlign: "center",
											border: "none",
										}}
									>
										<strong style={{ color: textColor }}>
											{d.getFullYear()} - {d.getFullYear() + 1}
										</strong>
									</td>
									<td
										style={{
											padding: "4px 8px",
											textAlign: "center",
											border: "none",
										}}
									>
										<strong style={{ color: textColor }}>
											{d.getFullYear() + 1} - {d.getFullYear() + 2}
										</strong>
									</td>
								</tr>
							</thead>
							<tbody>
								<tr style={{ background: "transparent" }}>
									<td
										style={{
											padding: "4px 8px",
											textAlign: "right",
											border: "none",
										}}
									>
										<label>Packets:</label>
									</td>
									{showPriorInventory && (
										<td style={{ padding: "4px 8px", border: "none" }}>
											<input
												type="text"
												value={unassignedPacketsPrior}
												readOnly
												style={{
													width: "7ch",
													textAlign: "center",
													backgroundColor: "transparent",
													color: textColor,
													border: "2px solid rgb(134,132,132)",
													borderRadius: "0.5em",
													padding: "4px",
												}}
											/>
										</td>
									)}
									<td style={{ padding: "4px 8px", border: "none" }}>
										<input
											type="text"
											value={unassignedPackets}
											readOnly
											style={{
												width: "7ch",
												textAlign: "center",
												backgroundColor: "transparent",
												color: textColor,
												border: "2px solid rgb(134,132,132)",
												borderRadius: "0.5em",
												padding: "4px",
											}}
										/>
									</td>
									<td style={{ padding: "4px 8px", border: "none" }}>
										<input
											type="text"
											value={unassignedPacketsNext}
											readOnly
											style={{
												width: "7ch",
												textAlign: "center",
												backgroundColor: "transparent",
												color: textColor,
												border: "2px solid rgb(134,132,132)",
												borderRadius: "0.5em",
												padding: "4px",
											}}
										/>
									</td>
								</tr>
								<tr style={{ background: "transparent" }}>
									<td
										style={{
											padding: "4px 8px",
											textAlign: "right",
											border: "none",
										}}
									>
										<label>Tags:</label>
									</td>
									{showPriorInventory && (
										<td style={{ padding: "4px 8px", border: "none" }}>
											<input
												type="text"
												value={unassignedTagsPrior}
												readOnly
												style={{
													width: "7ch",
													textAlign: "center",
													backgroundColor: "transparent",
													color: textColor,
													border: "2px solid rgb(134,132,132)",
													borderRadius: "0.5em",
													padding: "4px",
												}}
											/>
										</td>
									)}
									<td style={{ padding: "4px 8px", border: "none" }}>
										<input
											type="text"
											value={unassignedTags}
											readOnly
											style={{
												width: "7ch",
												textAlign: "center",
												backgroundColor: "transparent",
												color: textColor,
												border: "2px solid rgb(134,132,132)",
												borderRadius: "0.5em",
												padding: "4px",
											}}
										/>
									</td>
									<td style={{ padding: "4px 8px", border: "none" }}>
										<input
											type="text"
											value={unassignedTagsNext}
											readOnly
											style={{
												width: "7ch",
												textAlign: "center",
												backgroundColor: "transparent",
												color: textColor,
												border: "2px solid rgb(134,132,132)",
												borderRadius: "0.5em",
												padding: "4px",
											}}
										/>
									</td>
								</tr>
							</tbody>
						</table>
						{oldUnusedTags > 0 && (
							<div style={{ marginTop: "0.5rem", color: "#daa520", fontWeight: "bold", textAlign: "center" }}>
								Maintenance Due on old fiscal year.
							</div>
						)}
					</fieldset>
				</div>
			</div>
		</main>
	)
}

export default DogTags
